/**
 * URL Filter Utilities
 * Handles encoding/decoding of patient list filters to/from URL parameters
 */

import { subDays } from 'date-fns';

/**
 * Extracts essential data from complex objects for URL encoding
 */
const extractEssentialData = (arr, type) => {
  if (!arr || arr.length === 0) return '';
  
  switch (type) {
    case 'condition':
      // Extract ICD-10 codes with display names - format: "code:display|system" (system optional)
      return arr.map(item => {
        if (typeof item === 'string') return item; // Already encoded
        
        const code = item.code;
        const display = item.display;
        const system = item.system;
        
        // Create compact format: code:display|system
        let result = code;
        if (display && display !== code) {
          // URL encode display name and truncate if too long (max 50 chars)
          const truncatedDisplay = display.length > 50 ? display.substring(0, 47) + '...' : display;
          result = `${code}:${encodeURIComponent(truncatedDisplay)}`;
        }
        if (system) {
          result = `${result}|${system}`;
        }
        
        return result;
      }).join(',');
      
    case 'patientGroupIds':
      // Extract IDs with titles - format: "id:title"
      return arr.map(item => {
        if (typeof item === 'string') return item; // Already encoded
        
        const id = item.id;
        const title = item.title;
        
        // Create compact format: id:title
        let result = id;
        if (title && title !== id) {
          // URL encode title and truncate if too long (max 50 chars)
          const truncatedTitle = title.length > 50 ? title.substring(0, 47) + '...' : title;
          result = `${id}:${encodeURIComponent(truncatedTitle)}`;
        }
        
        return result;
      }).join(',');
      
    case 'careTeamIds':
      // Extract IDs with names - format: "id:name"
      return arr.map(item => {
        if (typeof item === 'string') return item; // Already encoded
        
        const id = item.id;
        const name = item.name;
        
        // Create compact format: id:name
        let result = id;
        if (name && name !== id) {
          // URL encode name and truncate if too long (max 50 chars)
          const truncatedName = name.length > 50 ? name.substring(0, 47) + '...' : name;
          result = `${id}:${encodeURIComponent(truncatedName)}`;
        }
        
        return result;
      }).join(',');

    case 'carePlan':
      // Extract IDs with titles - format: "id:title"
      return arr.map(item => {
        if (typeof item === 'string') return item; // Already encoded
        
        const id = item.id;
        const title = item.title;
        
        // Create compact format: id:title
        let result = id;
        if (title && title !== id) {
          // URL encode title and truncate if too long (max 50 chars)
          const truncatedTitle = title.length > 50 ? title.substring(0, 47) + '...' : title;
          result = `${id}:${encodeURIComponent(truncatedTitle)}`;
        }
        
        return result;
      }).join(',');
      
    default:
      // Simple arrays (risk, gender)
      return arr.join(',');
  }
};

/**
 * Reconstructs objects from essential data for filter state
 */
const reconstructObjects = (str, type) => {
  if (!str) return [];
  
  const values = str.split(',').filter(Boolean);
  
  switch (type) {
    case 'condition':
      // Reconstruct condition objects from encoded format: "code:display|system"
      return values.map(item => {
        const [codeAndDisplay, system] = item.split('|');
        const [code, encodedDisplay] = codeAndDisplay.split(':');
        
        const condition = {
          code,
          system: system || 'http://hl7.org/fhir/sid/icd-10', // Default ICD-10 system
        };
        
        // Add display name if present
        if (encodedDisplay) {
          condition.display = decodeURIComponent(encodedDisplay);
        }
        
        return condition;
      });
      
    case 'patientGroupIds':
      // Reconstruct patientGroup objects from encoded format: "id:title"
      return values.map(item => {
        const [id, encodedTitle] = item.split(':');
        
        const patientGroup = { id };
        
        // Add title if present
        if (encodedTitle) {
          patientGroup.title = decodeURIComponent(encodedTitle);
        }
        
        return patientGroup;
      });
      
    case 'careTeamIds':
      // Reconstruct careTeam objects from encoded format: "id:name"
      return values.map(item => {
        const [id, encodedName] = item.split(':');
        
        const careTeam = { id };
        
        // Add name if present
        if (encodedName) {
          careTeam.name = decodeURIComponent(encodedName);
        }
        
        return careTeam;
      });

    case 'carePlan':
      // Reconstruct carePlan objects from encoded format: "id:title"
      return values.map(item => {
        const [id, encodedTitle] = item.split(':');
        
        const carePlan = { id };
        
        // Add title if present
        if (encodedTitle) {
          carePlan.title = decodeURIComponent(encodedTitle);
        }
        
        return carePlan;
      });
      
    default:
      // Simple arrays
      return values;
  }
};

/**
 * Encodes filter object to URL search parameters
 * @param {Object} filters - Filter object from useSetFilters
 * @returns {URLSearchParams} - URL search parameters
 */
export const encodeFiltersToURL = (filters) => {
  const params = new URLSearchParams();
  
  // Remove internal fields that shouldn't be in URL
  const { _activityKey, ...cleanFilters } = filters;
  const filtersToEncode = cleanFilters;

  // Handle simple string/number values
  if (filtersToEncode.sort && filtersToEncode.sort !== 'RISK') {
    params.set('sort', filtersToEncode.sort);
  }
  
  if (filtersToEncode.startAge && filtersToEncode.startAge !== 0) {
    params.set('startAge', filtersToEncode.startAge.toString());
  }
  
  if (filtersToEncode.endAge && filtersToEncode.endAge !== 130) {
    params.set('endAge', filtersToEncode.endAge.toString());
  }

  if (filtersToEncode.name) {
    params.set('name', filtersToEncode.name);
  }

  // Handle array filters
  if (filtersToEncode.risk && filtersToEncode.risk.length > 0) {
    params.set('risk', extractEssentialData(filtersToEncode.risk, 'risk'));
  }

  if (filtersToEncode.gender && filtersToEncode.gender.length > 0) {
    params.set('gender', extractEssentialData(filtersToEncode.gender, 'gender'));
  }

  if (filtersToEncode.condition && filtersToEncode.condition.length > 0) {
    params.set('condition', extractEssentialData(filtersToEncode.condition, 'condition'));
  }

  if (filtersToEncode.patientGroupIds && filtersToEncode.patientGroupIds.length > 0) {
    params.set('patientGroupIds', extractEssentialData(filtersToEncode.patientGroupIds, 'patientGroupIds'));
  }

  if (filtersToEncode.careTeamIds && filtersToEncode.careTeamIds.length > 0) {
    params.set('careTeamIds', extractEssentialData(filtersToEncode.careTeamIds, 'careTeamIds'));
  }

  // Handle care plan filters
  if (Array.isArray(filtersToEncode.activeRequestGroupPlanDefinitionIds)) {
    if (filtersToEncode.activeRequestGroupPlanDefinitionIds.length > 0) {
      params.set('activeCarePlans', extractEssentialData(filtersToEncode.activeRequestGroupPlanDefinitionIds, 'carePlan'));
    } else {
      // Empty array means category is selected but no specific plans
      params.set('activeCarePlans', 'true');
    }
  }
  // Note: null values are not encoded (filter not selected)

  if (Array.isArray(filtersToEncode.completedRequestGroupPlanDefinitionIds)) {
    if (filtersToEncode.completedRequestGroupPlanDefinitionIds.length > 0) {
      params.set('completedCarePlans', extractEssentialData(filtersToEncode.completedRequestGroupPlanDefinitionIds, 'carePlan'));
    } else {
      // Empty array means category is selected but no specific plans
      params.set('completedCarePlans', 'true');
    }
  }
  // Note: null values are not encoded (filter not selected)

  if (filtersToEncode.hasNoRequestGroups) {
    params.set('hasNoCarePlans', 'true');
  }

  // Handle activity filter - store key instead of complex dates
  if (filtersToEncode.noActivity) {
    params.set('activity', 'no-activity');
  } else if (filtersToEncode.lastActivity) {
    // Determine activity key based on the date ranges
    const { max, min } = filtersToEncode.lastActivity;
    const now = new Date();
    
    if (!max && min) {
      // No max, only min - this is "today" (< 1 day ago)
      const daysDiff = Math.floor((now - new Date(min)) / (1000 * 60 * 60 * 24));
      if (daysDiff <= 1) params.set('activity', 'today');
    } else if (max && min) {
      // Both max and min - determine range
      const maxDays = Math.floor((now - new Date(max)) / (1000 * 60 * 60 * 24));
      const minDays = Math.floor((now - new Date(min)) / (1000 * 60 * 60 * 24));
      
      if (maxDays <= 1 && minDays <= 3) params.set('activity', 'days');
      else if (maxDays <= 3 && minDays <= 7) params.set('activity', 'week');
      else if (maxDays <= 7 && minDays <= 30) params.set('activity', 'month');
    } else if (max && !min) {
      // Only max, no min - this is "30+ days ago"
      const daysDiff = Math.floor((now - new Date(max)) / (1000 * 60 * 60 * 24));
      if (daysDiff >= 30) params.set('activity', 'more');
    }
  }

  // Handle alert filters
  if (filtersToEncode.hasActiveAlert) {
    params.set('hasActiveAlert', 'true');
  }

  if (filtersToEncode.hasNoActiveAlerts) {
    params.set('hasNoActiveAlerts', 'true');
  }

  return params;
};

/**
 * Decodes URL search parameters to filter object
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {Object} - Filter object compatible with useSetFilters
 */
export const decodeFiltersFromURL = (searchParams) => {
  const filters = {};

  // Handle simple string/number values
  const sort = searchParams.get('sort');
  if (sort) {
    filters.sort = sort;
  }

  const startAge = searchParams.get('startAge');
  if (startAge) {
    const parsed = parseInt(startAge, 10);
    if (!isNaN(parsed)) {
      filters.startAge = parsed;
    }
  }

  const endAge = searchParams.get('endAge');
  if (endAge) {
    const parsed = parseInt(endAge, 10);
    if (!isNaN(parsed)) {
      filters.endAge = parsed;
    }
  }

  const name = searchParams.get('name');
  if (name) {
    filters.name = name;
  }

  // Handle array filters
  const risk = searchParams.get('risk');
  if (risk) {
    filters.risk = reconstructObjects(risk, 'risk');
  }

  const gender = searchParams.get('gender');
  if (gender) {
    filters.gender = reconstructObjects(gender, 'gender');
  }

  const condition = searchParams.get('condition');
  if (condition) {
    filters.condition = reconstructObjects(condition, 'condition');
  }

  const patientGroupIds = searchParams.get('patientGroupIds');
  if (patientGroupIds) {
    filters.patientGroupIds = reconstructObjects(patientGroupIds, 'patientGroupIds');
  }

  const careTeamIds = searchParams.get('careTeamIds');
  if (careTeamIds) {
    filters.careTeamIds = reconstructObjects(careTeamIds, 'careTeamIds');
  }

  // Handle care plan filters
  const activeCarePlans = searchParams.get('activeCarePlans');
  if (activeCarePlans) {
    if (activeCarePlans === 'true') {
      // Category selected but no specific plans
      filters.activeRequestGroupPlanDefinitionIds = [];
    } else {
      filters.activeRequestGroupPlanDefinitionIds = reconstructObjects(activeCarePlans, 'carePlan');
    }
  }
  // Note: if parameter is absent, field remains undefined and will use default null value

  const completedCarePlans = searchParams.get('completedCarePlans');
  if (completedCarePlans) {
    if (completedCarePlans === 'true') {
      // Category selected but no specific plans
      filters.completedRequestGroupPlanDefinitionIds = [];
    } else {
      filters.completedRequestGroupPlanDefinitionIds = reconstructObjects(completedCarePlans, 'carePlan');
    }
  }
  // Note: if parameter is absent, field remains undefined and will use default null value

  const hasNoCarePlans = searchParams.get('hasNoCarePlans');
  if (hasNoCarePlans === 'true') {
    filters.hasNoRequestGroups = true;
  }

  // Handle activity filter - store the activity key directly instead of reconstructing dates
  const activity = searchParams.get('activity');
  if (activity) {
    // Store the activity key so it can be directly matched
    filters._activityKey = activity;
    
    // Also set the appropriate filter fields for backend compatibility
    if (activity === 'no-activity') {
      filters.noActivity = true;
    } else {
      const now = new Date();
      switch (activity) {
        case 'today':
          filters.lastActivity = {
            max: null,
            min: subDays(now, 1),
          };
          break;
        case 'days':
          filters.lastActivity = {
            max: subDays(now, 1),
            min: subDays(now, 3),
          };
          break;
        case 'week':
          filters.lastActivity = {
            max: subDays(now, 3),
            min: subDays(now, 7),
          };
          break;
        case 'month':
          filters.lastActivity = {
            max: subDays(now, 7),
            min: subDays(now, 30),
          };
          break;
        case 'more':
          filters.lastActivity = {
            max: subDays(now, 30),
            min: null,
          };
          break;
        default:
          // Unknown activity key, ignore
          break;
      }
    }
  }

  // Handle alert filters
  const hasActiveAlert = searchParams.get('hasActiveAlert');
  if (hasActiveAlert === 'true') {
    filters.hasActiveAlert = true;
  }

  const hasNoActiveAlerts = searchParams.get('hasNoActiveAlerts');
  if (hasNoActiveAlerts === 'true') {
    filters.hasNoActiveAlerts = true;
  }

  return filters;
};

/**
 * Validates that URL parameters don't exceed practical limits
 * @param {URLSearchParams} params - URL search parameters
 * @returns {boolean} - Whether URL is within acceptable size limits
 */
export const validateURLSize = (params) => {
  const url = params.toString();
  const maxLength = 1800; // Conservative limit, browsers support 2000+
  return url.length <= maxLength;
};

/**
 * Creates a shareable URL with current filters
 * @param {Object} filters - Current filter state
 * @param {string} baseURL - Base URL (optional, defaults to current page)
 * @returns {string} - Complete shareable URL
 */
export const createShareableURL = (filters, baseURL = window.location.origin + window.location.pathname) => {
  const params = encodeFiltersToURL(filters);
  
  if (!validateURLSize(params)) {
    console.warn('URL parameters exceed recommended size limit');
  }
  
  const queryString = params.toString();
  return queryString ? `${baseURL}?${queryString}` : baseURL;
};

/**
 * Determines if current filters differ from defaults
 * @param {Object} filters - Current filter state
 * @param {Object} defaultFilters - Default filter values
 * @returns {boolean} - Whether filters have been modified from defaults
 */
export const hasActiveFilters = (filters, defaultFilters) => {
  const encoded = encodeFiltersToURL(filters);
  const defaultEncoded = encodeFiltersToURL(defaultFilters);
  return encoded.toString() !== defaultEncoded.toString();
};